import axios from "axios";
import { installSessionExpiryInterceptor } from "./sessionExpiry";
import { installPlanRequiredInterceptor } from "./planRequired";

// When REACT_APP_BACKEND_URL is unset/empty (the Cloudflare Pages deploy —
// see docs/deploy-phase0.md), fall back to same-origin so /api/* hits the
// real D1-backed Hono app via frontend/functions/api/[[path]].ts (issue #38)
// instead of literally requesting "undefined/api". Same-origin also keeps
// the session cookie's SameSite=Lax assumption valid (api/src/auth/session.ts).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth is carried by an httpOnly `access_token` cookie set by the backend on
// /auth/login and /auth/register. Same-origin (ingress-routed) requests send
// the cookie automatically when `withCredentials` is true — the frontend
// never touches the JWT directly, which mitigates XSS token theft.
/**
 * Issue #336: every request gets a deadline.
 *
 * Axios has NO default timeout — `timeout: 0` means "wait forever" — so
 * before this a request that was accepted and then never answered (a
 * saturated worker holding the socket open, a captive-portal wifi that
 * swallows the response, a phone that loses the network mid-flight without
 * the TCP connection resetting) produced no response, no error, and no
 * rejection. Ever.
 *
 * That is survivable on a page that renders a card, because the card just
 * stays empty. It is not survivable on the app's two BLOCKING gates, which
 * is what this issue is about:
 *
 *   - `ProtectedRoute` holds every protected page behind `/auth/me`
 *     (`user === null` renders the "Getting your bearings…" spinner);
 *   - `OnboardingProvider` holds every player page behind
 *     `/players/me/onboarding` (`loading` renders the same spinner).
 *
 * Both of those already fail OPEN on a fast failure — a 4xx/5xx/reset lands
 * in their `catch`, and the player continues to /login or to the dashboard.
 * Neither could fail at all on a HANG: the promise stayed pending, `loading`
 * never resolved, and the player was held on a spinner with no way out short
 * of force-quitting the tab. Two indistinguishable spinners, one of them
 * permanent.
 *
 * A timeout is what converts the unhandleable case into the handled one. It
 * does not add a new failure path — it routes the hang into the failure path
 * every caller in this app already has.
 *
 * WHY 15s. It has to be longer than the worst honest response this product
 * produces (a cold Worker isolate plus a D1 read on a phone at a cricket
 * ground — hundreds of milliseconds, low single-digit seconds at the tail),
 * and short enough that a person who is stuck finds out while they are still
 * looking at the screen. It is instance-level rather than per-call so that a
 * route added later inherits the deadline instead of having to remember it.
 *
 * WHAT IT COSTS. `lib/fetchWithRetry.js` makes up to three attempts, so a
 * genuinely black-holed endpoint behind `getWithRetry` now takes up to
 * ~46s (3 x 15s + 1.6s backoff) to reach its error state instead of never
 * reaching it. Bounded and slow beats unbounded: the retry's own contract is
 * that the caller eventually renders an honest non-value, and until this
 * existed there were failure modes in which it never could.
 *
 * NOT a cure for a slow server, and deliberately not tuned as one. A request
 * that legitimately needs longer than this belongs on its own per-call
 * `{ timeout }`, argued for at that call site — uploads being the obvious
 * future case.
 *
 * ISSUE #436 ITEM 5 — "INHERITS" MEANS "GOES THROUGH THIS INSTANCE". There
 * is exactly one call at this API that does not: `pages/CompassCoachAI.jsx`
 * streams `text/event-stream` through a raw `fetch`, because axios in the
 * browser resolves on a fully buffered response and would delete the
 * streaming rather than merely slow it. It is left alone on purpose, and
 * `api.test.js` now enumerates it BY NAME — so the sentence above stays true
 * of every other route, and a new raw fetch fails a test instead of quietly
 * inheriting neither the deadline nor the 401 interceptor.
 */
export const API_TIMEOUT_MS = 15_000;

const api = axios.create({ baseURL: API, withCredentials: true, timeout: API_TIMEOUT_MS });

/**
 * Issue #396: the inbound half of #326's promise.
 *
 * A 401 from this API means "no live session" and nothing else — see
 * `lib/sessionExpiry.js` for why that is a property of
 * `api/src/auth/middleware.ts` rather than an assumption. Until this line
 * existed, a session that ended server-side while a tab was open left the SPA
 * rendering as signed in indefinitely.
 *
 * Installed HERE, on the shared instance, rather than in each caller: there
 * are ~40 call sites and the failure mode of a per-caller version is that the
 * one added next week forgets. It is the same "one gate everything already
 * passes through" argument the API makes for putting the confinement inside
 * `requireUser`.
 */
installSessionExpiryInterceptor(api, API);

// Issue #1671: the paywall's 402 `plan_required` opens Choose a plan. See `lib/planRequired.js`.
installPlanRequiredInterceptor(api);

// Resolve a file URL for use in <img>/<video> tags.
// - External URLs (http/https/data:) are returned unchanged
// - Backend-served files get BACKEND_URL prefixed. Because we're same-origin,
//   the browser automatically forwards the httpOnly auth cookie with the
//   image/video request — no query-string token is needed.
export function mediaUrl(url) {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${BACKEND_URL}${url}`;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
